import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchSignInMethodsForEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { useQuery } from '@tanstack/react-query';
import { auth } from '../firebase';
import { getProductPriceIds, getPlanByKey } from '../config/subscriptionPlans';
import { callFunction } from '../services/api';
import styles from './Auth.module.css';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseValidation, setLicenseValidation] = useState({
    isValid: null,
    message: '',
    loading: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [waitlistData, setWaitlistData] = useState({
    email: '',
    name: '',
    location: '',
    mls: ''
  });
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [validationAttempts, setValidationAttempts] = useState(0);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const MAX_VALIDATION_ATTEMPTS = 3;
  const navigate = useNavigate();
  const location = useLocation();

  const { data: statesData, isLoading: statesLoading } = useQuery({
    queryKey: ['supportedStates'],
    queryFn: async () => {
      const data = await callFunction('getSupportedStates');
      if (data.success && data.states) return data.states;
      throw new Error('Failed to load supported states');
    },
    staleTime: 5 * 60 * 1000,
  });
  const supportedStates = statesData ?? [];

  const { data: betaData } = useQuery({
    queryKey: ['betaStatus'],
    queryFn: async () => {
      const data = await callFunction('getBetaStatus');
      return data.available ? 'available' : 'full';
    },
    staleTime: 5 * 60 * 1000,
  });
  const betaStatus = betaData ?? 'checking';

  // Handle selected plan from navigation state or URL params
  useEffect(() => {
    if (location.state?.selectedPlan) {
      setSelectedPlan(location.state.selectedPlan);
    } else {
      // Check URL parameters for plan selection
      const urlParams = new URLSearchParams(location.search);
      const planParam = urlParams.get('plan');
      if (planParam) {
        // Get plan from config
        const plan = getPlanByKey(planParam);
        if (plan) {
          setSelectedPlan(plan);
        }
      }
    }
  }, [location.state, location.search]);


  // Validate license using ARELLO
  const validateLicense = async () => {
    if (!jurisdiction || !licenseNumber || !firstName || !lastName) {
      setLicenseValidation({
        isValid: false,
        message: 'Please fill in all required fields',
        loading: false
      });
      return;
    }

    // Check validation attempt limit
    if (validationAttempts >= MAX_VALIDATION_ATTEMPTS) {
      setLicenseValidation({
        isValid: false,
        message: 'Maximum validation attempts reached. Please contact support.',
        loading: false
      });
      return;
    }

    // Client-side validation for license number
    const licenseRegex = /^[A-Za-z0-9\s.-]+$/;
    if (!licenseNumber.trim() || !licenseRegex.test(licenseNumber)) {
      setLicenseValidation({
        isValid: false,
        message: 'License number must contain only letters, numbers, spaces, dots, and hyphens',
        loading: false
      });
      return;
    }

    try {
      setLicenseValidation({ isValid: null, message: '', loading: true });

      const data = await callFunction('validateLicense', {
        jurisdiction,
        licenseNumber: licenseNumber.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      if (data.success && data.isValid) {
        setLicenseValidation({
          isValid: true,
          message: data.message,
          loading: false,
          validatedLicenseData: data.validatedLicenseData || null,
        });
        setValidationAttempts(0);
      } else {
        setLicenseValidation({
          isValid: false,
          message: data.message || 'License validation failed',
          loading: false,
          validatedLicenseData: null,
        });
        setValidationAttempts(prev => prev + 1);
      }
    } catch (error) {
      const isRateLimited = error?.code === 'functions/resource-exhausted';
      setLicenseValidation({
        isValid: false,
        message: isRateLimited
          ? 'Too many attempts. Please try again later.'
          : 'License validation failed. Please try again.',
        loading: false,
      });
      setValidationAttempts(prev => prev + 1);
    }
  };



  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation: Check required fields
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // Validation: Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validation: Password length
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    // Validation: Password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validation: License must be validated before proceeding
    if (licenseValidation.isValid !== true) {
      setError('Please validate your license before signing up');
      setLoading(false);
      return;
    }

    // Validation: Subscription plan must be selected
    if (!selectedPlan) {
      setError('Please select a subscription plan');
      setLoading(false);
      return;
    }

    // Validation: Terms and conditions must be accepted
    if (!acceptedTerms) {
      setError('Please accept the terms and conditions to continue');
      setLoading(false);
      return;
    }

    try {
      // Check if email already exists in Firebase Auth
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.length > 0) {
        setError('An account with this email already exists. Please sign in instead.');
        setLoading(false);
        return;
      }
      
      // Step 1: Create account via signup callable
      const signupData = await callFunction('signup', {
        email,
        password,
        firstName,
        lastName,
        jurisdiction,
        licenseNumber,
        selectedPlan: { key: selectedPlan.key, name: selectedPlan.name },
        acceptedTerms,
        termsAcceptedAt: new Date().toISOString(),
        validatedLicenseNumber: licenseValidation.validatedLicenseData?.licenseNumber || licenseNumber,
        validatedJurisdiction: licenseValidation.validatedLicenseData?.jurisdiction || jurisdiction,
        validatedLicenseeName: licenseValidation.validatedLicenseData?.licenseeName || `${firstName} ${lastName}`,
        validatedLicenseStatus: licenseValidation.validatedLicenseData?.status || null,
      });


      // Step 2: Sign in to get ID token

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();


      // Step 3: Create authenticated checkout session
      const checkoutData = await callFunction('createCheckoutSession', {
        priceId: getProductPriceIds()[selectedPlan.key],
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/signup`
      });
      
      const { url } = checkoutData;

      
      // Step 4: Redirect to Stripe Checkout
      window.location.href = url;
      
    } catch (error) {

      setError(error.message || 'Failed to complete signup. Please try again.');
      setLoading(false);
    }
  };


  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();

    if (!waitlistData.email || !waitlistData.name || !waitlistData.location || !waitlistData.mls) {
      setError('Please fill in all fields');
      return;
    }

    setWaitlistLoading(true);
    setError('');

    try {
      await callFunction('joinWaitlist', {
        email: waitlistData.email,
        name: waitlistData.name,
        location: waitlistData.location,
        mls: waitlistData.mls,
      });

      setWaitlistSuccess(true);
    } catch (error) {
      console.error('Waitlist error:', error);
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleWaitlistInputChange = (field, value) => {
    setWaitlistData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Show loading state while checking public preview status
  if (betaStatus === 'checking') {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <h2>Loading</h2>
          <p>Preparing your signup.</p>
        </div>
      </div>
    );
  }

  // Show waitlist success state
  if (waitlistSuccess) {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <h2>Thanks for your interest!</h2>
          <p>We'll notify you when we launch. You're on our priority list for early access.</p>
          <button
            className={styles.authButton}
            onClick={() => navigate('/')}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Show waitlist form when public preview is full
  if (betaStatus === 'full') {
    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
                          <h2>Public Preview is Full</h2>
                <p>Our Public Preview is full. We're testing with a small group to ensure the best experience.</p>
          <p>Join our waitlist to be notified when we launch!</p>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <form onSubmit={handleWaitlistSubmit} className={styles.authForm}>
            <div className={styles.formGroup}>
              <label htmlFor="waitlist-email">Email</label>
              <input
                type="email"
                id="waitlist-email"
                value={waitlistData.email}
                onChange={(e) => handleWaitlistInputChange('email', e.target.value)}
                required
                placeholder="Enter your email"
                disabled={waitlistLoading}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="waitlist-name">Full Name</label>
              <input
                type="text"
                id="waitlist-name"
                value={waitlistData.name}
                onChange={(e) => handleWaitlistInputChange('name', e.target.value)}
                required
                placeholder="Enter your full name"
                disabled={waitlistLoading}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="waitlist-location">Operating Location</label>
              <input
                type="text"
                id="waitlist-location"
                value={waitlistData.location}
                onChange={(e) => handleWaitlistInputChange('location', e.target.value)}
                required
                placeholder="Where are you operating from?"
                disabled={waitlistLoading}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="waitlist-mls">MLS Subscriptions</label>
              <input
                type="text"
                id="waitlist-mls"
                value={waitlistData.mls}
                onChange={(e) => handleWaitlistInputChange('mls', e.target.value)}
                required
                placeholder="What MLSs are you subscribed to?"
                disabled={waitlistLoading}
              />
            </div>

            <button type="submit" className={styles.authButton} disabled={waitlistLoading}>
              {waitlistLoading ? 'Joining Waitlist...' : 'Join Waitlist'}
            </button>
          </form>

          <div className={styles.authLinks}>
            <p>
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/signin')}
                className={styles.linkButton}
              >
                Sign In
              </button>
            </p>
            <button 
              onClick={() => navigate('/')}
              className={styles.linkButton}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show normal signup form when beta is available
  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <h2>Join Vates Domus AI</h2>
        <p className={styles.authSubtitle}>Exclusive access for licensed real estate professionals</p>
        
        {selectedPlan && (
          <div className={styles.selectedPlanInfo}>
            <h3>Selected Plan: {selectedPlan.name}</h3>
            <p>${selectedPlan.price}/month • {selectedPlan.credits} AI Credits</p>
            {selectedPlan.key === 'foundation' && (
              <p style={{ 
                marginTop: '0.5rem', 
                fontSize: '0.875rem', 
                color: '#059669',
                fontWeight: '500'
              }}>
                💰 Use promo code <strong>VATES50</strong> at checkout for 50% off your first month!
              </p>
            )}
          </div>
        )}
        
        {error && <div className={styles.errorMessage}>{error}</div>}
        
        <form onSubmit={handleSignUp} className={styles.authForm}>
          <div className={styles.formGroup}>
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="Enter your first name"
              disabled={loading}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder="Enter your last name"
              disabled={loading}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="jurisdiction">State/Jurisdiction</label>
            <select
              id="jurisdiction"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              required
              disabled={loading || statesLoading}
            >
              <option value="">Select your state</option>
              {supportedStates.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
            {statesLoading && <p className={styles.formHelp}>Loading supported states...</p>}
            {supportedStates.length === 0 && !statesLoading && (
              <p className={`${styles.formHelp} ${styles.error}`}>
                No supported states available. Please contact support to request your state.
              </p>
            )}
            {!statesLoading && (
              <p className={styles.formHelp}>
                This product is currently only available in North Carolina, but we are adding new jurisdictions.{' '}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => navigate('/contact?reason=state-support')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                    marginLeft: '0.25rem',
                    fontSize: 'inherit',
                    fontFamily: 'inherit'
                  }}
                >
                  Request your state be moved up the list
                </button>
              </p>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="licenseNumber">License Number</label>
            <input
              type="text"
              id="licenseNumber"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              required
              placeholder="Enter your license number as it appears on your license"
              disabled={loading}
            />
            <p className={styles.formHelp}>
              Enter your license number exactly as it appears on your real estate license
            </p>
          </div>
          
          {jurisdiction && licenseNumber && firstName && lastName && (
            <div className={styles.formGroup}>
              <button
                type="button"
                onClick={validateLicense}
                disabled={licenseValidation.loading}
                className={`${styles.authButton} ${styles.secondary}`}
              >
                {licenseValidation.loading ? 'Validating License...' : 'Validate License'}
              </button>
              {licenseValidation.message && (
                <p className={`${styles.formHelp} ${licenseValidation.isValid ? styles.success : styles.error}`}>
                  {licenseValidation.message}
                </p>
              )}
              {validationAttempts >= MAX_VALIDATION_ATTEMPTS && (
                <div className={styles.formGroup}>
                  <div className="max-attempts-message">
                    <p>Having trouble validating your license?</p>
                    <button
                      type="button"
                      onClick={() => navigate('/contact')}
                      className={`${styles.authButton} ${styles.secondary}`}
                    >
                      Contact Support
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password (min 8 characters)"
              disabled={loading}
              minLength={8}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm your password"
              disabled={loading}
              minLength={8}
            />
          </div>
          
          {licenseValidation.isValid !== true && (
            <div className="validation-required-message" style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '0.75rem',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontWeight: '500' }}>
                ⚠️ Please validate your license before proceeding with signup
              </p>
            </div>
          )}
          
          {/* Terms and Conditions Checkbox */}
          <div className={styles.formGroup} style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem',
              lineHeight: '1.4'
            }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={loading}
                style={{ 
                  marginTop: '0.2rem',
                  transform: 'scale(1.1)'
                }}
              />
              <span>
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => {
                    const modal = document.getElementById('termsModal');
                    if (modal) modal.style.display = 'block';
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 'inherit'
                  }}
                >
                  Software License Agreement
                </button>
                {' '}and understand that my account will only be created after successful payment.
              </span>
            </label>
          </div>
          
          <button 
            type="submit" 
            className={styles.authButton}
            disabled={loading || licenseValidation.isValid !== true || !selectedPlan || !acceptedTerms || !password || !confirmPassword || password.length < 8 || password !== confirmPassword}
          >
            {loading ? 'Creating Account...' : 
             licenseValidation.isValid !== true ? 'Validate License to Continue' :
             !selectedPlan ? 'Select a Plan to Continue' :
             !acceptedTerms ? 'Accept Terms to Continue' :
             !password || password.length < 8 ? 'Enter Password (min 8 characters)' :
             password !== confirmPassword ? 'Passwords Do Not Match' :
             `Sign Up & Continue to Payment - ${selectedPlan.name}`}
          </button>
        </form>
        
        <div className={styles.authLinks}>
          <p>
            Already have an account?{' '}
            <button 
              onClick={() => navigate('/signin')}
              className={styles.linkButton}
            >
              Sign In
            </button>
          </p>
          <button 
            onClick={() => navigate('/')}
            className={styles.linkButton}
          >
            Back to Home
          </button>
        </div>
      </div>
      
      {/* Terms and Conditions Modal */}
      <div id="termsModal" style={{
        display: 'none',
        position: 'fixed',
        zIndex: 1000,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        overflow: 'auto'
      }}>
        <div style={{
          backgroundColor: '#fefefe',
          margin: '2% auto',
          padding: '20px',
          border: '1px solid #888',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: '1px solid #eee',
            paddingBottom: '10px'
          }}>
            <h2 style={{ margin: 0, color: '#333' }}>Software License Agreement</h2>
            <button
              onClick={() => {
                const modal = document.getElementById('termsModal');
                if (modal) modal.style.display = 'none';
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999',
                padding: '0',
                width: '30px',
                height: '30px'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#333',
            whiteSpace: 'pre-line'
          }}>
            {`SOFTWARE LICENSE AGREEMENT

This Software License Agreement ("Agreement") is entered into by and between Vates Domus AI, Inc., a Delaware corporation ("Licensor"), and the individual or entity accessing or using the Software ("Licensee").

1. DEFINITIONS

1.1 Software means Vates Domus AI, Licensor's proprietary AI real estate platform, including property search tools (chat, map search, MLS ID, listing ID, address, or zip code), Comparative Market Analysis ("CMA") features, AI analysis of subject and comparable properties, and all related functionality, updates, and enhancements.

1.2 User means the real estate agent, broker, or authorized end user accessing the Software under this Agreement.

1.3 Output means any data, reports, text, analysis, recommendations, or other information generated by the Software.

2. LICENSE GRANT

Subject to the terms of this Agreement, Licensor grants Licensee a limited, non-exclusive, non-transferable, revocable license to access and use the Software solely for Licensee's internal professional real estate activities.

3. LICENSE RESTRICTIONS

Licensee shall not, and shall not permit others to:

(a) copy, modify, adapt, translate, or create derivative works of the Software;

(b) reverse engineer, decompile, disassemble, or otherwise attempt to derive source code from the Software;

(c) rent, lease, sublicense, sell, assign, or otherwise commercially exploit the Software or Outputs, except in the ordinary course of Licensee's professional services;

(d) use the Software in violation of any applicable law, MLS rules, or professional standards;

(e) interfere with or disrupt the integrity, performance, or security of the Software.

4. INTELLECTUAL PROPERTY RIGHTS

All rights, title, and interest in and to the Software, including all intellectual property rights, remain the sole property of Licensor. Licensee acquires no ownership rights in the Software. Any suggestions, feedback, or improvements provided by Licensee may be used by Licensor without restriction or compensation.

5. USER RESPONSIBILITIES

5.1 Professional Judgment. The Software is intended as a tool to assist real estate professionals. Licensee is solely responsible for exercising independent judgment in evaluating properties, preparing analyses, and advising clients.

5.2 AI Limitations. Licensee acknowledges that artificial intelligence may produce inaccurate, incomplete, or "hallucinated" results. Outputs should not be relied upon as definitive, error-free, or a substitute for human expertise.

5.3 Compliance. Licensee is responsible for ensuring compliance with all applicable laws, regulations, MLS rules, licensing requirements, and fiduciary duties when using the Software.

6. DATA USE & PRIVACY

6.1 User Data. Licensee grants Licensor a limited right to collect, store, process, and use data input by Licensee into the Software for purposes of providing, maintaining, and improving the Software.

6.2 Privacy Policy. Use of the Software is also subject to Licensor's Privacy Policy, as updated from time to time.

7. FEES & PAYMENT (if applicable)

7.1 Licensee agrees to pay all fees in accordance with Licensor's pricing terms, whether per subscription, per seat, or per CMA generated.

7.2 Fees are non-refundable except as required by law.

7.3 Licensor may suspend or terminate access to the Software for non-payment.

8. DISCLAIMERS

THE SOFTWARE AND OUTPUTS ARE PROVIDED "AS IS" AND "AS AVAILABLE." LICENSOR DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT. LICENSOR MAKES NO WARRANTY THAT OUTPUTS WILL BE COMPLETE, RELIABLE, OR ERROR-FREE.

9. LIMITATION OF LIABILITY

TO THE MAXIMUM EXTENT PERMITTED BY LAW, LICENSOR SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOSS OF BUSINESS, OR LOSS OF DATA, ARISING OUT OF OR RELATING TO LICENSEE'S USE OF THE SOFTWARE OR OUTPUTS, EVEN IF LICENSOR HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

10. INDEMNIFICATION

Licensee agrees to indemnify, defend, and hold harmless Licensor and its officers, directors, employees, and agents from and against any claims, damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of Licensee's use of the Software, misuse of Outputs, violation of this Agreement, or violation of applicable laws or regulations.

11. ARBITRATION AGREEMENT

11.1 Agreement to Arbitrate. Any dispute, claim, or controversy arising out of or relating to this Agreement or the Software shall be resolved exclusively by binding arbitration administered by the American Arbitration Association ("AAA") in accordance with its Commercial Arbitration Rules.

11.2 Venue and Governing Law. The arbitration shall take place in Delaware, USA, and this Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles.

11.3 Waiver of Class Actions. The parties agree that arbitration will be conducted solely on an individual basis, and not in a class, consolidated, or representative action.

11.4 Final and Binding. The arbitrator's decision shall be final and binding. Judgment on the award may be entered in any court of competent jurisdiction.

11.5 Exception. Licensor may seek injunctive relief in court to protect its intellectual property.

12. TERM AND TERMINATION

This Agreement is effective upon Licensee's first access to the Software and continues until terminated. Licensor may suspend or terminate access if Licensee violates this Agreement. Upon termination, Licensee must immediately cease all use of the Software.

13. GENERAL PROVISIONS

13.1 Severability. If any provision of this Agreement is held unenforceable, the remaining provisions remain in effect.

13.2 Survival. Sections relating to IP rights, disclaimers, limitations of liability, indemnification, and arbitration survive termination.

13.3 Force Majeure. Licensor shall not be liable for delays or failures due to events beyond its reasonable control, including natural disasters, internet outages, cyberattacks, or government actions.

13.4 Export Compliance. Licensee shall not export or re-export the Software in violation of U.S. export laws.

13.5 Entire Agreement. This Agreement constitutes the entire agreement between the parties regarding the Software and supersedes prior understandings.`}
          </div>
          
          <div style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #eee',
            textAlign: 'center'
          }}>
            <button
              onClick={() => {
                setAcceptedTerms(true);
                const modal = document.getElementById('termsModal');
                if (modal) modal.style.display = 'none';
              }}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '16px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              I Accept
            </button>
            <button
              onClick={() => {
                const modal = document.getElementById('termsModal');
                if (modal) modal.style.display = 'none';
              }}
              style={{
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default SignUp; 